import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { GolfConfig, HealthConfig, OuraStatus, TeeOnConfig } from '@jordan/types';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Input,
  PageHeader,
  SelectMenu,
  SettingsPanel,
  SettingsRow,
  Spinner,
  TagsInput,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { apiDelete, apiGet, apiPut } from '../lib/api';

export function HealthSettingsPage() {
  const { search } = useLocation();
  const [status, setStatus] = useState<OuraStatus | null>(null);
  const [cfg, setCfg] = useState<HealthConfig | null>(null);
  const [golf, setGolf] = useState<GolfConfig | null>(null);
  const [teeon, setTeeon] = useState<TeeOnConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = () => apiGet<OuraStatus>('/api/health/oura/status').then(setStatus);

  useEffect(() => {
    loadStatus().catch(() => setError('Failed to load Oura connection status.'));
    apiGet<HealthConfig>('/api/config/health')
      .then(setCfg)
      .catch(() => setError('Failed to load Health settings.'));
    apiGet<GolfConfig>('/api/config/golf')
      .then(setGolf)
      .catch(() => setError('Failed to load Golf settings.'));
    apiGet<TeeOnConfig>('/api/config/teeon')
      .then(setTeeon)
      .catch(() => setError('Failed to load Tee-On settings.'));
  }, []);

  // Surface the OAuth callback result (redirects back here with a query flag).
  useEffect(() => {
    const p = new URLSearchParams(search);
    if (p.get('oura') === 'connected') setMessage('Oura Ring connected.');
    else if (p.get('error') === 'oura') setError("Couldn't connect Oura — please try again.");
  }, [search]);

  const connect = async () => {
    setError(null);
    try {
      const { url } = await apiGet<{ url: string }>('/api/health/oura/connect');
      window.location.href = url;
    } catch {
      setError("Oura isn't configured yet (missing OAuth client credentials in env).");
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await apiDelete('/api/health/oura');
      await loadStatus();
      setMessage('Oura Ring disconnected.');
    } catch {
      setError('Could not disconnect.');
    } finally {
      setBusy(false);
    }
  };

  const set = <K extends keyof HealthConfig>(k: K, v: HealthConfig[K]) =>
    setCfg((p) => (p ? { ...p, [k]: v } : p));
  const num = (k: keyof HealthConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(k, Number(e.target.value) as never);

  const saveSettings = async () => {
    if (!cfg) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<HealthConfig>('/api/config/health', cfg);
      setCfg(saved);
      setMessage('Oura Ring settings saved.');
    } catch {
      setError('Could not save — check the values are within range.');
    } finally {
      setBusy(false);
    }
  };

  const setG = <K extends keyof GolfConfig>(k: K, v: GolfConfig[K]) =>
    setGolf((p) => (p ? { ...p, [k]: v } : p));
  const numG = (k: keyof GolfConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setG(k, Number(e.target.value) as never);

  const saveGolf = async () => {
    if (!golf) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await apiPut<GolfConfig>('/api/config/golf', golf);
      setGolf(saved);
      setMessage('Golf settings saved.');
    } catch {
      setError('Could not save Golf settings — check the values are within range.');
    } finally {
      setBusy(false);
    }
  };

  const setT = <K extends keyof TeeOnConfig>(k: K, v: TeeOnConfig[K]) =>
    setTeeon((p) => (p ? { ...p, [k]: v } : p));
  const numT = (k: keyof TeeOnConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setT(k, Number(e.target.value) as never);

  const saveTeeon = async () => {
    if (!teeon) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      // bookingEnabled is server-managed (view-only for now) — omit it from the write.
      const saved = await apiPut<TeeOnConfig>('/api/config/teeon', {
        baseUrl: teeon.baseUrl,
        defaultCourseCode: teeon.defaultCourseCode,
        defaultCourseName: teeon.defaultCourseName,
        memberName: teeon.memberName,
        defaultCourseGroupId: teeon.defaultCourseGroupId,
        sessionTtlMinutes: teeon.sessionTtlMinutes,
        bookingWindowDays: teeon.bookingWindowDays,
        teeSheetPageSize: teeon.teeSheetPageSize,
      });
      setTeeon(saved);
      setMessage('Tee Times settings saved.');
    } catch {
      setError('Could not save Tee Times settings — check the values are within range.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">Health</span>
          </>
        }
        breadcrumb={
          <Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Configuration' }, { label: 'Health' }]} />
        }
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        {!status ? (
          <Spinner />
        ) : (
          <SettingsPanel
            title="Oura Ring"
            description="Connect your Oura Ring account and tune how its data is shown on the Health dashboard."
            action={
              status.connected ? (
                <Button variant="light" className="text-danger" icon="ph-link-break" disabled={busy} onClick={disconnect}>
                  Disconnect
                </Button>
              ) : (
                <Button variant="primary" icon="ph-link" disabled={!status.configured} onClick={connect}>
                  Connect Oura Ring
                </Button>
              )
            }
          >
            <SettingsRow title="Connection" description="OAuth connection to the Oura Cloud API.">
              {!status.configured ? (
                <span className="text-muted">
                  Not configured — set <code>OURA_CLIENT_ID</code> / <code>OURA_CLIENT_SECRET</code> in the environment.
                </span>
              ) : status.connected ? (
                <div className="d-flex align-items-center gap-2">
                  <Badge variant="success">Connected</Badge>
                  {status.email && <span className="text-muted">{status.email}</span>}
                </div>
              ) : (
                <Badge variant="secondary">Not connected</Badge>
              )}
            </SettingsRow>

            {cfg && (
              <>
                <SettingsRow title="API base URL" description="Oura data API endpoint. Change only if the API version changes.">
                  <Input value={cfg.ouraApiBaseUrl} onChange={(e) => set('ouraApiBaseUrl', e.target.value)} />
                </SettingsRow>

                <SettingsRow title="Default period" description="The aggregation period the dashboard opens with.">
                  <div style={{ maxWidth: 220 }}>
                    <SelectMenu
                      icon="ph-calendar-blank"
                      value={cfg.ouraDefaultPeriod}
                      onChange={(v) => set('ouraDefaultPeriod', v as HealthConfig['ouraDefaultPeriod'])}
                      options={[
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'hourly', label: 'Hourly' },
                        { value: '15min', label: '15 minutes' },
                        { value: '5min', label: '5 minutes' },
                      ]}
                    />
                  </div>
                </SettingsRow>

                <SettingsRow title="Default range (days)" description="How many days the main range spans in Daily view.">
                  <div style={{ maxWidth: 200 }}>
                    <Input type="number" value={cfg.ouraDefaultRangeDays} onChange={num('ouraDefaultRangeDays')} />
                  </div>
                </SettingsRow>

                <SettingsRow title="Overview span (days)" description="How much history the overview strip at the top covers.">
                  <div style={{ maxWidth: 200 }}>
                    <Input type="number" value={cfg.ouraOverviewDays} onChange={num('ouraOverviewDays')} />
                  </div>
                </SettingsRow>

                <SettingsRow title="" description="">
                  <Button variant="primary" disabled={busy} onClick={saveSettings}>
                    Save changes
                  </Button>
                </SettingsRow>
              </>
            )}
          </SettingsPanel>
        )}

        {golf && (
          <SettingsPanel
            title="Golf"
            description="Tune the golf dashboards — the dispersion-scatter fairway/rough bands and axis, the BigQuery dataset for shot data, the importable activity types, and how the per-course Stats page reads scorecards."
          >
            <SettingsRow
              title="Fairway half-width (yds)"
              description="Half-width of the green fairway band on the dispersion scatter (± from centre)."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={golf.fairwayHalfWidthYds} onChange={numG('fairwayHalfWidthYds')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Rough half-width (yds)"
              description="Half-width of the amber rough band; shots beyond this count as out of bounds (red)."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={golf.roughHalfWidthYds} onChange={numG('roughHalfWidthYds')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Scatter min axis (yds)"
              description="Minimum ± dispersion shown on the scatter x-axis; it expands automatically for wider shots."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={golf.scatterMinAxisYds} onChange={numG('scatterMinAxisYds')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="BigQuery dataset"
              description="Dataset holding the imported shot rows. Letters, numbers and underscores only."
            >
              <div style={{ maxWidth: 280 }}>
                <Input value={golf.bigQueryDataset} onChange={(e) => setG('bigQueryDataset', e.target.value)} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Season start date"
              description="The Scores dashboard omits all rounds and statistics before this date."
            >
              <div style={{ maxWidth: 220 }}>
                <Input
                  type="date"
                  value={golf.seasonStartDate}
                  onChange={(e) => setG('seasonStartDate', e.target.value)}
                />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Stats: rounds included by default"
              description="Which rounds the Stats page opens with — every round at the course, or only those since the season start."
            >
              <div style={{ maxWidth: 220 }}>
                <SelectMenu
                  icon="ph-funnel"
                  value={golf.statsDefaultScope}
                  onChange={(v) => setG('statsDefaultScope', v as GolfConfig['statsDefaultScope'])}
                  options={[
                    { value: 'all', label: 'All time' },
                    { value: 'season', label: 'This season' },
                  ]}
                />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Stats: scorecards read per course"
              description="Cap on how many of a course's rounds have their scorecard read for the hole-by-hole breakdown (newest first). Higher means more complete stats and a slower page."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={golf.statsMaxRounds} onChange={numG('statsMaxRounds')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Stats: scorecards fetched at once"
              description="How many scorecard requests run in parallel against Golf Canada (1–20)."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={golf.statsConcurrency} onChange={numG('statsConcurrency')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Stats: scorecard cache (minutes)"
              description="How long a fetched scorecard is reused before Golf Canada is asked again. 0 disables caching."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={golf.statsCacheMinutes} onChange={numG('statsCacheMinutes')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Activity types"
              description="Types offered when importing a session (press Enter to add)."
            >
              <TagsInput
                defaultTags={golf.activityTypes}
                placeholder="Add type…"
                separators={[',']}
                onChange={(tags) => setG('activityTypes', tags)}
              />
            </SettingsRow>

            <SettingsRow title="" description="">
              <Button variant="primary" disabled={busy} onClick={saveGolf}>
                Save changes
              </Button>
            </SettingsRow>
          </SettingsPanel>
        )}

        {teeon && (
          <SettingsPanel
            title="Tee Times (Tee-On)"
            description="Settings for the Golf → Tee Times tee-sheet view. Sign-in credentials are secrets and live in the environment (TEEON_USERNAME / TEEON_PASSWORD), not here."
          >
            <SettingsRow title="Default course code" description="Tee-On course code the tee sheet opens to (e.g. PCTN for Picton).">
              <div style={{ maxWidth: 200 }}>
                <Input value={teeon.defaultCourseCode} onChange={(e) => setT('defaultCourseCode', e.target.value)} />
              </div>
            </SettingsRow>

            <SettingsRow title="Default course name" description="Display name shown above the tee sheet.">
              <div style={{ maxWidth: 320 }}>
                <Input value={teeon.defaultCourseName} onChange={(e) => setT('defaultCourseName', e.target.value)} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Member name"
              description="Your name as it appears on tee sheets. Tee times you're booked into are highlighted. Leave blank to disable."
            >
              <div style={{ maxWidth: 320 }}>
                <Input value={teeon.memberName} onChange={(e) => setT('memberName', e.target.value)} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Booking window (days)"
              description="How many days ahead the tee-sheet date picker offers."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={teeon.bookingWindowDays} onChange={numT('bookingWindowDays')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Records per page"
              description="Rows shown per page on the tee-sheet table. Set to 0 to show every time slot with no pagination."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" min={0} value={teeon.teeSheetPageSize} onChange={numT('teeSheetPageSize')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Session lifetime (minutes)"
              description="How long a captured Tee-On sign-in is reused before a fresh browser login is forced."
            >
              <div style={{ maxWidth: 200 }}>
                <Input type="number" value={teeon.sessionTtlMinutes} onChange={numT('sessionTtlMinutes')} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Course group ID"
              description="Tee-On CourseGroupID for the default course. Advanced — change only if the course changes."
            >
              <div style={{ maxWidth: 200 }}>
                <Input value={teeon.defaultCourseGroupId} onChange={(e) => setT('defaultCourseGroupId', e.target.value)} />
              </div>
            </SettingsRow>

            <SettingsRow
              title="Servlet base URL"
              description="Tee-On servlet host + path. Advanced — change only if Tee-On moves."
            >
              <div style={{ maxWidth: 420 }}>
                <Input value={teeon.baseUrl} onChange={(e) => setT('baseUrl', e.target.value)} />
              </div>
            </SettingsRow>

            <SettingsRow title="Booking" description="Whether Jordan can place bookings on Tee-On.">
              <Badge variant={teeon.bookingEnabled ? 'success' : 'secondary'}>
                {teeon.bookingEnabled ? 'Enabled' : 'View-only'}
              </Badge>
              <span className="text-muted ms-2">Booking isn’t implemented yet — this stays view-only for now.</span>
            </SettingsRow>

            <SettingsRow title="" description="">
              <Button variant="primary" disabled={busy} onClick={saveTeeon}>
                Save changes
              </Button>
            </SettingsRow>
          </SettingsPanel>
        )}
      </div>
    </AppChrome>
  );
}
